/**
 * Ask eve beside a workflow's chart: the chat view in a column on the right
 * of the Workflows page, on the external chat host, so nothing in it moves
 * the page. Each workflow opens the person's newest chat opened from it, or
 * a new one; every chat opened here is bound to the workflow, so its turns
 * tell eve which chart "this" is (`apps/server/src/amoreg/workflowTurnContext.ts`).
 *
 * The column is a sibling of the canvas, not a layer over it: the canvas
 * measures only its own column, so framing a step never aims under the chat.
 * The chat shows bare (`ChatPresentation`): the panel's bar is its only
 * header, and an empty chat offers examples for the chart on screen.
 *
 * @module components/workflows/AskEvePanel
 */
import { scopeProjectRef, scopeThreadRef } from "@amoreg/client-runtime/environment";
import { ProjectId, type ScopedThreadRef, ThreadId, type WorkflowChat } from "@amoreg/contracts";
import { useAtomValue } from "@effect/atom-react";
import { ArrowLeftIcon, CheckIcon, HistoryIcon, SquarePenIcon, XIcon } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DraftId, markPromotedDraftThreadByRef, useComposerDraftStore } from "../../composerDraftStore";
import { claimGuideShortcut } from "../../guideBus";
import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { shortcutLabelForCommand } from "../../keybindings";
import { cn } from "../../lib/utils";
import { useThread, useThreadShell } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useOrgs } from "../../state/orgs";
import { primaryServerKeybindingsAtom } from "../../state/server";
import { bindWorkflowChat, readWorkflowChats } from "../../state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { HostedChatView } from "../ChatView";
import { threadHasStarted } from "../ChatView.logic";
import { type ChatPresentation, ChatHostContext, ChatPresentationContext, useExternalChatHost } from "../chat/chatHost";
import { waitForDraftHeroTransition } from "../chat/draftHeroTransition";
import { Button } from "../ui/button";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { AmoregMark } from "../sidebar/SidebarChrome";
import { toastManager } from "../ui/toast";
import type { ChartKeys } from "./canvas/WorkflowCanvas";
import {
  CANVAS_MIN_WIDTH,
  clampPanelWidth,
  FIRST_VISITS_STORAGE_KEY,
  FirstVisits,
  firstVisit,
  PANEL_CHART_VIEWS,
  type PanelChartState,
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  PANEL_PREFS_STORAGE_KEY,
  type PanelChat,
  panelChatTarget,
  panelPerson,
  panelPrefsFor,
  PanelPrefsByPerson,
  pickPanelChat,
  pageHoldsPanelBeside,
  recentPanelThreads,
  withPanelPrefs,
  workflowChatSlot,
} from "./AskEvePanel.logic";
import { isUndoChord } from "./workflowCanvas.logic";

const RESIZE_STEP = 24;
/** How long a new chat's first message waits for its binding. */
export const FIRST_SEND_BIND_WAIT_MS = 2000;
const NO_PREFS: PanelPrefsByPerson = {};
const NO_VISITS: ReadonlyArray<string> = [];

/** Ask the panel to open on a chat (the person's newest when null), focused on its composer or a waiting card's Approve. */
export interface PanelRequest {
  readonly threadId: string | null;
  readonly focus: "composer" | "approve";
  readonly key: number;
}

/**
 * A press in a focus container on something that cannot take focus, or
 * whose handler kept the browser from moving focus (a pan, a drag),
 * focuses the container, so the keys that follow are its own. A press the
 * browser handles focuses the nearest focusable element, the container at
 * the farthest.
 */
export function focusContainerOnPress(event: ReactPointerEvent<HTMLElement>) {
  const container = event.currentTarget;
  // React bubbles a portal's press here; the portal is not in the container.
  if (!(event.target instanceof Node) || !container.contains(event.target)) return;
  if (!event.nativeEvent.defaultPrevented || container.contains(document.activeElement)) return;
  container.focus({ preventScroll: true });
}

/**
 * Keys pressed in the panel, in a popup it opened (React bubbles a portal's
 * events through the component that rendered it) or on the Ask eve button:
 * the panel's, so the chart skips them. Recorded rather than stopped: React's
 * stopPropagation also stops the native event, which the chat's own window
 * listeners and the page's shortcuts still need.
 */
const panelKeys = new WeakSet<Event>();
const TEXT_FIELD = "input, textarea, [contenteditable='true']";

export const claimKeyForPanel = (event: ReactKeyboardEvent) => {
  panelKeys.add(event.nativeEvent);
  // Outside a text field nothing would handle it but the browser's own undo,
  // which reverts the last field edited anywhere on the page, a step's too.
  if (isUndoChord(event) && !(event.target instanceof Element && event.target.closest(TEXT_FIELD))) event.preventDefault();
};

/**
 * The browser's own undo keeps one stack for the whole page: in a field with
 * nothing left to undo, ⌘Z reverts the field edited before it, a step's
 * name in the chart included. Each field undoes only its own typing. The
 * event is stopped as well as cancelled: the composer's editor (Lexical) runs
 * its own undo on a `beforeinput` it receives, cancelled or not.
 */
const keepUndoInFocusedField = (event: InputEvent) => {
  if (event.inputType !== "historyUndo" && event.inputType !== "historyRedo") return;
  const active = document.activeElement;
  const target = event.target;
  if (active === target || (active instanceof HTMLElement && active.isContentEditable && target instanceof Node && active.contains(target))) return;
  event.preventDefault();
  event.stopImmediatePropagation();
};

/**
 * Whether the panel is open and how wide, remembered per person across
 * reloads, and which keys the chart hears. The guide's shortcut (⌘J) opens
 * and closes the panel while the page is mounted.
 */
export function useAskEvePanel(
  chartKeys: RefObject<ChartKeys | null>,
  visit?: { readonly workflowId: string; readonly state: PanelChartState },
) {
  const { viewer } = useOrgs();
  const person = panelPerson(viewer);
  const [stored, setStored] = useLocalStorage(PANEL_PREFS_STORAGE_KEY, NO_PREFS, PanelPrefsByPerson);
  const prefs = panelPrefsFor(stored, person);
  const open = prefs.open;
  const width = clampPanelWidth(prefs.width);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // A request is spent once the panel closes: reopened, it focuses the composer.
  const [request, setRequest] = useState<PanelRequest | null>(null);
  const close = useCallback(() => {
    setRequest(null);
    setStored((current) => withPanelPrefs(current, person, { open: false }));
  }, [person, setStored]);
  const openPanel = useCallback(
    () => setStored((current) => withPanelPrefs(current, person, { open: true })),
    [person, setStored],
  );
  const toggle = useCallback(() => (open ? close() : openPanel()), [close, open, openPanel]);
  useEffect(() => claimGuideShortcut(toggle), [toggle]);

  const openOn = useCallback(
    (threadId: string | null, focus: PanelRequest["focus"]) => {
      setRequest((last) => ({ threadId, focus, key: (last?.key ?? 0) + 1 }));
      openPanel();
    },
    [openPanel],
  );

  // A blank chart opens the panel the first time this person opens the
  // workflow in this browser, and never again by itself.
  const [visits, setVisits] = useLocalStorage(FIRST_VISITS_STORAGE_KEY, NO_VISITS, FirstVisits);
  const workflowId = visit?.workflowId ?? null;
  useEffect(() => {
    if (!workflowId || !visit) return;
    const first = firstVisit(visits, { person, workflowId, state: visit.state });
    if (!first) return;
    setVisits(first.visits);
    if (first.opens) openPanel();
    // Once per workflow and person: the chart changing later is no first visit.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [person, workflowId]);

  const phone = useIsMobile();
  // Whether the row the panel shares with the chart holds both; null until
  // measured, when the phone query decides.
  const [roomBeside, setRoomBeside] = useState<boolean | null>(null);
  useLayoutEffect(() => {
    const row = panelRef.current?.parentElement;
    if (!row) return;
    setRoomBeside(pageHoldsPanelBeside(row.clientWidth));
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setRoomBeside(pageHoldsPanelBeside(entry.contentRect.width));
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const resize = useCallback(
    (width: number) => setStored((current) => withPanelPrefs(current, person, { width })),
    [person, setStored],
  );
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const beside = roomBeside ?? !phone;
  const mobile = !beside;
  const chartHears = useCallback(
    (event: ReactKeyboardEvent) => !(open && mobile) && !panelKeys.has(event.nativeEvent),
    [mobile, open],
  );
  // The body is under no React root, so keys typed with focus fallen there
  // reach only the window. Open, focus may have fallen from the chat, so
  // Backspace and Delete are nobody's; the rest can be undone, by the chart
  // alone: the browser's own undo would revert the chat's typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target !== document.body) return;
      if (open && isUndoChord(event)) event.preventDefault();
      if (open && mobile) {
        if (event.key !== "Escape") return;
        event.preventDefault();
        close();
      } else if (!open || (event.key !== "Backspace" && event.key !== "Delete")) chartKeys.current?.(event);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [chartKeys, close, mobile, open]);
  useEffect(() => {
    window.addEventListener("beforeinput", keepUndoInFocusedField, true);
    return () => window.removeEventListener("beforeinput", keepUndoInFocusedField, true);
  }, []);
  return {
    open,
    width,
    /** What the panel takes from the chart's width; the chart refits when it changes. */
    besideWidth: open && !mobile ? width : 0,
    mobile,
    shortcutLabel: shortcutLabelForCommand(keybindings, "guide.toggle"),
    panelRef,
    buttonRef,
    chartHears,
    toggle,
    close,
    resize,
    request,
    openOn,
  };
}

/** The unsent draft holding this thread, if this browser has one. */
function draftIdForThread(threadRef: ScopedThreadRef): DraftId | null {
  const drafts = useComposerDraftStore.getState().draftThreadsByThreadKey;
  for (const [draftId, draft] of Object.entries(drafts)) {
    if (draft.environmentId === threadRef.environmentId && draft.threadId === threadRef.threadId && !draft.promotedTo) {
      return DraftId.make(draftId);
    }
  }
  return null;
}

const notLinked = (message: string) =>
  toastManager.add({
    type: "error",
    title: "This chat is not linked to the workflow",
    description: `${message} eve will not know which chart "this" means.`,
  });

/**
 * The panel's chat: picked when the page opens, then moved by New chat,
 * Recent chats and the chat itself. Only an open panel starts a new chat, so
 * viewing a workflow writes nothing.
 */
function usePanelChat(organizationId: string, workflowId: string, open: boolean) {
  const environmentId = usePrimaryEnvironmentId();
  const [chats, setChats] = useState<ReadonlyArray<WorkflowChat>>([]);
  const [chat, setChat] = useState<PanelChat | null>(null);
  const currentTarget = useCallback(() => panelChatTarget(chat), [chat]);
  const handleNewThread = useNewThreadHandler(undefined, currentTarget);

  const link = useCallback(
    async (threadId: string) => {
      const bound = await bindWorkflowChat(organizationId, workflowId, threadId);
      if (bound.ok) setChats((current) => [bound.value, ...current.filter((entry) => entry.threadId !== threadId)]);
      return bound;
    },
    [organizationId, workflowId],
  );
  const bind = useCallback(
    async (threadId: string) => {
      const first = await link(threadId);
      const bound = first.ok ? first : await link(threadId);
      if (!bound.ok) notLinked(bound.message);
    },
    [link],
  );

  // A new chat is bound before it is shown, so its first message already
  // carries the workflow.
  const startNewChat = useCallback(async () => {
    if (!environmentId) return;
    let draftId: DraftId | null;
    try {
      draftId = await handleNewThread(scopeProjectRef(environmentId, ProjectId.make(organizationId)), {
        navigate: false,
        draftSlot: workflowChatSlot(organizationId, workflowId),
      });
    } catch {
      toastManager.add({ type: "error", title: "A new chat could not be started. Try again." });
      return;
    }
    const draft = draftId ? useComposerDraftStore.getState().getDraftSession(draftId) : null;
    if (!draftId || !draft) return;
    await bind(draft.threadId);
    setChat({ kind: "draft", draftId, threadRef: scopeThreadRef(draft.environmentId, draft.threadId) });
  }, [bind, environmentId, handleNewThread, organizationId, workflowId]);

  const [read, setRead] = useState(false);
  // The workflow whose chats were read: once per workflow, when its panel
  // first opens, so a closed panel costs the page nothing.
  const chatsReadFor = useRef<string | null>(null);
  useEffect(() => {
    const key = workflowChatSlot(organizationId, workflowId);
    if (!open || !environmentId || chatsReadFor.current === key) return;
    chatsReadFor.current = key;
    void readWorkflowChats(organizationId, workflowId).then((result) => {
      if (chatsReadFor.current !== key) return;
      const list = result.kind === "ready" ? result.value : [];
      setChats(list);
      setChat((current) => current ?? pickPanelChat(list, environmentId, draftIdForThread));
      setRead(true);
    });
  }, [environmentId, open, organizationId, workflowId]);

  const starting = useRef(false);
  useEffect(() => {
    if (!open || !read || chat !== null || starting.current) return;
    starting.current = true;
    void startNewChat().finally(() => {
      starting.current = false;
    });
  }, [chat, open, read, startNewChat]);

  // A new chat's first message starts its thread; the panel then follows the
  // thread, as the draft page does by moving to the thread page.
  const draftRef = chat?.kind === "draft" ? chat.threadRef : null;
  const draftThread = useThread(draftRef, { waitForShell: true });
  const draftStarted = threadHasStarted(draftThread);
  useEffect(() => {
    if (!draftRef || draftThread === null) return;
    markPromotedDraftThreadByRef(draftRef);
    if (!draftStarted) return;
    let cancelled = false;
    void waitForDraftHeroTransition().then(() => {
      if (cancelled) return;
      setChat({ kind: "thread", threadRef: draftRef });
      setChats((current) =>
        current.map((entry) => (entry.threadId === draftRef.threadId ? { ...entry, started: true } : entry)),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [draftRef, draftStarted, draftThread]);

  const openThread = useCallback(
    async (threadRef: ScopedThreadRef) => {
      setChat({ kind: "thread", threadRef });
      await bind(threadRef.threadId);
    },
    [bind],
  );
  const openDraft = useCallback(
    async (draftId: DraftId) => {
      const draft = useComposerDraftStore.getState().getDraftSession(draftId);
      if (!draft) return;
      setChat({ kind: "draft", draftId, threadRef: scopeThreadRef(draft.environmentId, draft.threadId) });
      await bind(draft.threadId);
    },
    [bind],
  );

  // A new chat is bound again before its first message, since a draft kept
  // past a day loses its binding to the next bind's prune. The message waits
  // only so long; a slower bind finishes behind it.
  const prepareFirstSend = useCallback(
    (threadRef: ScopedThreadRef) =>
      Promise.race([
        bind(threadRef.threadId),
        new Promise<void>((resolve) => setTimeout(resolve, FIRST_SEND_BIND_WAIT_MS)),
      ]),
    [bind],
  );

  const recent = environmentId ? recentPanelThreads(chats, environmentId) : [];
  const boundAt = new Map(chats.map((entry) => [entry.threadId, entry.boundAt]));
  return { chat, recent, boundAt, environmentId, startNewChat, openThread, openDraft, prepareFirstSend };
}

function RecentChatItem({
  threadRef,
  boundAt,
  current,
  onOpen,
}: {
  readonly threadRef: ScopedThreadRef;
  readonly boundAt: string | undefined;
  readonly current: boolean;
  readonly onOpen: (threadRef: ScopedThreadRef) => void;
}) {
  const shell = useThreadShell(threadRef);
  return (
    <MenuItem onClick={() => onOpen(threadRef)} data-ask-eve-recent={threadRef.threadId}>
      <CheckIcon aria-hidden className={cn("size-3.5", current ? "opacity-100" : "opacity-0")} />
      <span className="min-w-0 flex-1 truncate">{shell?.title || "Untitled chat"}</span>
      {boundAt ? <span className="shrink-0 text-[12px] text-muted-foreground tabular-nums">{formatRelativeTimeLabel(boundAt)}</span> : null}
    </MenuItem>
  );
}

/** The left edge: drag, or arrow keys when focused, to change the width. */
function ResizeRail({
  panelRef,
  width,
  onCommit,
}: {
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly width: number;
  readonly onCommit: (width: number) => void;
}) {
  const drag = useRef<{
    readonly startX: number;
    readonly startWidth: number;
    readonly pageWidth: number;
    readonly pointerId: number;
    width: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  // Read from the rail's own parent: the panel's ref is set only after its children's effects.
  const railRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(width);
  useLayoutEffect(() => {
    const panel = railRef.current?.parentElement;
    if (!panel) return;
    setShown(panel.offsetWidth);
    const observer = new ResizeObserver(() => setShown(panel.offsetWidth));
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);
  const pageWidth = () => panelRef.current?.parentElement?.clientWidth ?? window.innerWidth;
  // The width follows the pointer on the element itself, so the chart is not
  // re-rendered on every move; it is saved once, on release.
  const widthAt = (clientX: number) => {
    const start = drag.current;
    return start ? clampPanelWidth(start.startWidth + start.startX - clientX, start.pageWidth) : shown;
  };
  // Escape mid-drag is the drag's alone: it puts back the saved width, and
  // the panel and the chart stay as they were. A panel hidden mid-drag (⌘J,
  // another window) saves the width it was drawn at, so the drawn width and
  // the saved one never disagree.
  useEffect(() => {
    if (!dragging) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const held = drag.current;
      if (event.key !== "Escape" || !held) return;
      event.preventDefault();
      event.stopPropagation();
      drag.current = null;
      setDragging(false);
      railRef.current?.releasePointerCapture(held.pointerId);
      if (panelRef.current) panelRef.current.style.width = `${width}px`;
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      const held = drag.current;
      drag.current = null;
      if (held) onCommit(held.width);
    };
  }, [dragging, onCommit, panelRef, width]);
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !panelRef.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startWidth = panelRef.current.offsetWidth;
    drag.current = { startX: event.clientX, startWidth, pageWidth: pageWidth(), pointerId: event.pointerId, width: startWidth };
    setDragging(true);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const held = drag.current;
    if (!held || !panelRef.current) return;
    held.width = widthAt(event.clientX);
    panelRef.current.style.width = `${held.width}px`;
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const next = widthAt(event.clientX);
    drag.current = null;
    setDragging(false);
    onCommit(next);
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === "ArrowLeft" ? RESIZE_STEP : event.key === "ArrowRight" ? -RESIZE_STEP : 0;
    if (delta === 0) return;
    event.preventDefault();
    onCommit(clampPanelWidth(shown + delta, pageWidth()));
  };
  return (
    <div
      ref={railRef}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize Ask eve"
      aria-valuemin={PANEL_MIN_WIDTH}
      aria-valuemax={PANEL_MAX_WIDTH}
      aria-valuenow={shown}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      className="group absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none outline-none"
      data-ask-eve-resize=""
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-[3px] w-px bg-transparent transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary group-active:bg-primary motion-reduce:transition-none"
      />
    </div>
  );
}

/** The empty chat's examples, pinned above the composer: each sends its text as typed. */
function PanelExamples({ examples, onPick }: { readonly examples: ReadonlyArray<string>; readonly onPick: (text: string) => void }) {
  return (
    <ul className="flex flex-col gap-1.5" aria-label="Examples" data-ask-eve-examples="">
      {examples.map((example) => (
        <li key={example}>
          <button
            type="button"
            onClick={() => onPick(example)}
            className="flex min-h-8 w-full items-center rounded-md border border-border bg-background px-2.5 py-2 text-left text-[13px] leading-4 text-foreground transition-colors duration-150 hover:bg-accent focus-visible:bg-accent focus-visible:outline-2 focus-visible:outline-ring motion-reduce:transition-none"
            data-ask-eve-example=""
          >
            {example}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Ask eve's tab on the chart's right edge while the panel is closed: it
 * slides out under the pointer or focus and opens the panel. Its dot pings
 * while eve edits this workflow.
 */
export function AskEveTab({ onOpen, live }: { readonly onOpen: () => void; readonly live: boolean }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      onKeyDown={claimKeyForPanel}
      aria-label="Open Ask eve"
      className="absolute top-1/2 right-0 z-20 flex -translate-y-1/2 translate-x-1 flex-col items-center gap-2 rounded-l-xl border border-r-0 border-border bg-background px-1.5 py-3 text-xs font-medium text-muted-foreground shadow-md transition-[translate,color] duration-150 ease-power3-out hover:translate-x-0 hover:text-foreground focus-visible:translate-x-0 focus-visible:outline-2 focus-visible:outline-ring motion-reduce:transition-none"
      data-ask-eve-tab=""
    >
      {live ? (
        <span role="img" aria-label="eve is editing" className="absolute -top-1 -left-1 flex size-2" data-ask-eve-tab-live="">
          <span className="absolute inset-0 rounded-full bg-(--workflow-change) opacity-60 motion-safe:animate-ping" />
          <span className="relative size-2 rounded-full bg-(--workflow-change)" />
        </span>
      ) : null}
      <span aria-hidden className="inline-flex [&_svg]:h-4">
        <AmoregMark />
      </span>
      <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap">Ask eve</span>
    </button>
  );
}

/** A waiting card's Approve in the composer: an option, or an approval's own button. */
const approveButtonIn = (root: HTMLElement) =>
  [...root.querySelectorAll<HTMLButtonElement>("button")].find((button) => button.textContent?.trim().startsWith("Approve")) ?? null;

export function AskEvePanel({
  organizationId,
  workflowId,
  chart,
  request,
  open,
  width,
  mobile,
  shortcutLabel,
  panelRef,
  buttonRef,
  onClose,
  onResize,
}: {
  readonly organizationId: string;
  readonly workflowId: string;
  /** The chart on screen, for the empty chat's examples, the composer's placeholder and its note. */
  readonly chart: PanelChartState;
  readonly request: PanelRequest | null;
  readonly open: boolean;
  readonly width: number;
  readonly mobile: boolean;
  readonly shortcutLabel: string | null;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  readonly onClose: () => void;
  readonly onResize: (width: number) => void;
}) {
  const { chat, recent, boundAt, environmentId, startNewChat, openThread, openDraft, prepareFirstSend } = usePanelChat(
    organizationId,
    workflowId,
    open,
  );
  const draftOf = useCallback(
    (threadRef: ScopedThreadRef) =>
      chat?.kind === "draft" && chat.threadRef.threadId === threadRef.threadId ? chat.draftId : null,
    [chat],
  );
  const owns = useCallback(
    (target: EventTarget | null) => target instanceof Node && (panelRef.current?.contains(target) ?? false),
    [panelRef],
  );
  // Whether the panel held focus as its chat changed, read while rendering,
  // before the old chat leaves the page and drops its focus on the body.
  const shownChat = useRef(chat);
  const heldFocusAtChange = useRef(false);
  if (shownChat.current !== chat) {
    shownChat.current = chat;
    heldFocusAtChange.current = owns(document.activeElement);
  }
  const heldFocus = useCallback(() => heldFocusAtChange.current, []);
  const host = useExternalChatHost({ openDraft, openThread, owns, heldFocus, prepareFirstSend, draftOf });
  const view = PANEL_CHART_VIEWS[chart];
  const presentation = useMemo<ChatPresentation>(
    () => ({
      chrome: "bare",
      placeholder: view.placeholder,
      ...(view.note ? { note: view.note } : {}),
      emptyState: (send) => <PanelExamples examples={view.examples} onPick={send} />,
    }),
    [view],
  );

  // A chat asked for by name (a link to the chart, the change bar) replaces the one on screen.
  const shownThread = chat?.threadRef.threadId ?? null;
  const shownThreadRef = useRef(shownThread);
  shownThreadRef.current = shownThread;
  useEffect(() => {
    const threadId = request?.threadId;
    if (!threadId || !environmentId || shownThreadRef.current === threadId) return;
    void openThread(scopeThreadRef(environmentId, ThreadId.make(threadId)));
  }, [environmentId, openThread, request]);

  // Whether focus is in the panel or a popup it opened: React's focus events
  // bubble through portals, where DOM containment cannot follow.
  const focusInside = useRef(false);
  const wasOpen = useRef(open);
  // However the panel hides (✕, Escape, ⌘J, another window), focus it held,
  // or focus fallen to the body, goes to the Ask eve button. In the layout
  // phase, once the chat that held focus is gone: no browser has to report
  // the focus a removed element took with it (WebKit fires no focusout).
  useLayoutEffect(() => {
    const hiding = wasOpen.current && !open;
    wasOpen.current = open;
    if (!hiding) return;
    const active = document.activeElement;
    if (focusInside.current || active === null || active === document.body) buttonRef.current?.focus();
    focusInside.current = false;
  }, [buttonRef, open]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    claimKeyForPanel(event);
    // A popup, the composer's suggestions or a rename that handled Escape
    // first says so, and the panel stays open.
    if (event.key !== "Escape" || event.defaultPrevented || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    onClose();
  };

  // Focus the composer the moment it is in the page once the panel opens: the
  // chat mounts in a task of its own, and its own focus waits two frames. A
  // chat that changes under an open panel (a new chat becoming its thread)
  // does not focus it again: focus may be in a step's field by then. Review
  // on the change bar asks for the waiting card's Approve instead.
  const focusTarget = request?.focus ?? "composer";
  useEffect(() => {
    const root = panelRef.current;
    if (!open || !root) return;
    const focus = () => {
      const target =
        focusTarget === "approve" ? approveButtonIn(root) : root.querySelector<HTMLElement>('[data-testid="composer-editor"]');
      target?.focus();
      return target !== null;
    };
    if (focus()) return;
    const observer = new MutationObserver(() => {
      if (focus()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [focusTarget, open, panelRef, request?.key]);

  return (
    <div
      ref={panelRef}
      role="complementary"
      aria-label="Ask eve"
      hidden={!open}
      tabIndex={-1}
      onPointerDown={focusContainerOnPress}
      onKeyDown={onKeyDown}
      onFocus={() => {
        focusInside.current = true;
      }}
      onBlur={() => {
        focusInside.current = false;
      }}
      style={mobile ? undefined : { width, maxWidth: `max(${PANEL_MIN_WIDTH}px, calc(100% - ${CANVAS_MIN_WIDTH}px))` }}
      className={cn(
        "flex min-h-0 flex-col bg-background outline-none",
        mobile ? "absolute inset-0 z-40" : "relative shrink-0 border-l border-border",
      )}
      data-ask-eve-panel={chat?.threadRef.threadId ?? ""}
    >
      {/* A hidden panel holds nothing, so none of its menus stays open. */}
      {open ? (
        <>
          {mobile ? null : <ResizeRail panelRef={panelRef} width={width} onCommit={onResize} />}
          <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border ps-2 pe-1.5 md:ps-3" data-ask-eve-bar="">
            {mobile ? (
              <Button size="sm" variant="ghost" onClick={onClose} data-ask-eve-chart="">
                <ArrowLeftIcon />
                Chart
              </Button>
            ) : (
              <span className="text-[13px] font-medium text-foreground">Ask eve</span>
            )}
            <span className="min-w-0 flex-1" />
            <Menu>
              <MenuTrigger
                render={<Button size="icon-sm" variant="ghost" aria-label="Recent chats" title="Recent chats" disabled={recent.length === 0} />}
              >
                <HistoryIcon />
              </MenuTrigger>
              <MenuPopup align="end" className="w-72">
                <MenuGroup>
                  <MenuGroupLabel>This workflow</MenuGroupLabel>
                  {recent.map((threadRef) => (
                    <RecentChatItem
                      key={threadRef.threadId}
                      threadRef={threadRef}
                      boundAt={boundAt.get(threadRef.threadId)}
                      current={chat?.threadRef.threadId === threadRef.threadId}
                      onOpen={(next) => void openThread(next)}
                    />
                  ))}
                </MenuGroup>
              </MenuPopup>
            </Menu>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="New chat"
              title="New chat"
              onClick={() => void startNewChat()}
              disabled={chat?.kind === "draft"}
              data-ask-eve-new=""
            >
              <SquarePenIcon />
            </Button>
            {mobile ? null : (
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Close Ask eve"
                title={shortcutLabel ? `Close (${shortcutLabel})` : "Close"}
                onClick={onClose}
                data-ask-eve-close=""
              >
                <XIcon />
              </Button>
            )}
          </div>
          <div className="flex min-h-0 flex-1 flex-col motion-safe:animate-canvas-fade-in motion-safe:[animation-duration:120ms]">
            {chat ? (
              <ChatHostContext.Provider value={host}>
                <ChatPresentationContext.Provider value={presentation}>
                {chat.kind === "thread" ? (
                  <HostedChatView
                    key={chat.threadRef.threadId}
                    environmentId={chat.threadRef.environmentId}
                    threadId={chat.threadRef.threadId}
                    routeKind="server"
                  />
                ) : (
                  <HostedChatView
                    key={chat.draftId}
                    environmentId={chat.threadRef.environmentId}
                    threadId={chat.threadRef.threadId}
                    routeKind="draft"
                    draftId={chat.draftId}
                  />
                )}
                </ChatPresentationContext.Provider>
              </ChatHostContext.Provider>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
